import { useMemo, useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import { DropdownItem, MDSImage } from '@marqvision/mds-v2';
import { BoundingBox } from '@marqvision/shared';
import { MarqCommonModelCoordinate } from '~/swagger/data-contracts';

// useEffect cleanup 시 이미지 이벤트 정리를 위한 타입
type ExtendedHTMLImageElement = HTMLImageElement & {
  __cleanup?: () => void;
};

export type DropdownItemWithBboxList = DropdownItem<number> & {
  bbox_list: MarqCommonModelCoordinate[];
};

export const useTrademarkPreview = (list: DropdownItem<number>[]) => {
  const [previewItem, setPreviewItem] = useState<DropdownItemWithBboxList | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const tmLogoImageMap = useMemo(() => {
    const map = new Map<string, DropdownItemWithBboxList>();
    list.forEach((item) => {
      if (item.imgUrl) {
        map.set(item.imgUrl, item as DropdownItemWithBboxList);
      }
    });
    return map;
  }, [list]);

  const handleDropdownOpen = () => {
    setIsDropdownOpen(true);
  };

  const handleDropdownClose = () => {
    setIsDropdownOpen(false);
    setPreviewItem(null);
    setAnchorEl(null);
  };

  // 드롭다운 이미지에 hover 이벤트 추가
  useEffect(() => {
    if (!isDropdownOpen || tmLogoImageMap.size === 0) return;

    const setupImageHovers = () => {
      const container = document.querySelector('.mds-dropdown-scroll');
      if (!container) return;

      container.querySelectorAll('img').forEach((img) => {
        const extendedImg = img as ExtendedHTMLImageElement;
        if (extendedImg.__cleanup) return;

        const imageItem = tmLogoImageMap.get(img.src);
        if (!imageItem) return;

        const showPreview = () => {
          setPreviewItem(imageItem);
          setAnchorEl(container as HTMLElement);
        };

        const hidePreview = () => {
          setPreviewItem(null);
          setAnchorEl(null);
        };

        img.addEventListener('mouseenter', showPreview);
        img.addEventListener('mouseleave', hidePreview);
        extendedImg.__cleanup = () => {
          img.removeEventListener('mouseenter', showPreview);
          img.removeEventListener('mouseleave', hidePreview);
        };
      });
    };

    // DOM 변경 감지 및 이벤트 설정
    const observer = new MutationObserver(() => {
      setTimeout(setupImageHovers, 50);
    });

    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(setupImageHovers, 100);

    return () => {
      observer.disconnect();
      document.querySelectorAll('img').forEach((img) => {
        const extendedImg = img as ExtendedHTMLImageElement;
        if (extendedImg.__cleanup) {
          extendedImg.__cleanup();
          delete extendedImg.__cleanup;
        }
      });
    };
  }, [isDropdownOpen, tmLogoImageMap]);

  return {
    anchorEl,
    previewItem,
    onOpen: handleDropdownOpen,
    onClose: handleDropdownClose,
  };
};

type TrademarkedLogoImagePreviewProps = {
  url?: string;
  bBoxList?: MarqCommonModelCoordinate[];
};

const useStyles = makeStyles(({ color }) => ({
  container: {
    width: '240px',
    maxWidth: '100%',
    borderRadius: '8px',
    boxShadow: '0px 0px 2px rgba(0, 0, 0, 0.16), 0px 8px 16px rgba(0, 0, 0, 0.2)',
    backgroundColor: color.bg.fill.neutral.default.normal,
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1300,
  },
  imageBox: {
    position: 'relative',
    width: '240px',
    height: '240px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& img': {
      objectFit: 'contain',
      width: '100%',
      height: '100%',
    },
  },
}));

export const TrademarkedLogoImagePreview = (props: TrademarkedLogoImagePreviewProps) => {
  const classes = useStyles();
  const { url, bBoxList } = props;

  const [size, setSize] = useState<{ height: number; width: number }>();
  const handleGetScale = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const target = event.target as HTMLImageElement;
    setSize({
      height: target.naturalHeight,
      width: target.naturalWidth,
    });
  };

  if (!url) {
    return null;
  }

  return (
    <div className={classes.container}>
      <div className={classes.imageBox}>
        <MDSImage src={url} alt={''} width="100%" height="100%" objectFit="contain" onLoad={bBoxList && handleGetScale}>
          {bBoxList &&
            size &&
            bBoxList.map((bBox, index) => (
              <BoundingBox
                key={index}
                borderWidth={3}
                detectionData={{
                  bb_x1: bBox.x1,
                  bb_y1: bBox.y1,
                  bb_x2: bBox.x2,
                  bb_y2: bBox.y2,
                  image_width: size?.width,
                  image_height: size?.height,
                }}
              />
            ))}
        </MDSImage>
      </div>
    </div>
  );
};
